import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const API_URL = API_BASE_URL;

const NO_RETRY_STATUSES = [400, 401, 403, 404, 422];
const retryDelay = (attempt: number) => 500 * Math.pow(2, attempt);

export const api = axios.create({
  baseURL: API_URL,
  timeout: 10_000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor ────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const { useAuthStore } = require('../store/authStore');
    const { activeTenantId } = useAuthStore.getState();
    if (activeTenantId && activeTenantId !== '__all__') {
      config.headers['X-Impersonate-Tenant'] = activeTenantId;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ── Token refresh state ────────────────────────────────────────────────────
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function onRefreshFailed() {
  refreshSubscribers = [];
}

// ── Response interceptor with auto-refresh ─────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as any;
    const status = error.response?.status;

    // ── 401: attempt silent token refresh ──
    if (status === 401 && !config?._retry) {
      const { useAuthStore } = require('../store/authStore');
      const { refreshToken, logout, setTokens } = useAuthStore.getState();

      // No refresh token, or the refresh endpoint itself returned 401 → logout
      if (!refreshToken || config?.url === '/auth/refresh') {
        logout();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Another request is already refreshing — queue this one
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            config.headers.Authorization = `Bearer ${newToken}`;
            config._retry = true;
            resolve(api(config));
          });
        });
      }

      config._retry = true;
      isRefreshing = true;

      try {
        const res = await api.post('/auth/refresh', {
          refresh_token: refreshToken,
        });
        const { access_token, refresh_token: newRefreshToken } = res.data;

        setTokens(access_token, newRefreshToken);
        onRefreshed(access_token);
        isRefreshing = false;

        config.headers.Authorization = `Bearer ${access_token}`;
        return api(config);
      } catch {
        isRefreshing = false;
        onRefreshFailed();
        logout();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }

    // ── Retry: network errors + 429 + 5xx ──
    const isRetryable =
      !status ||
      status === 429 ||
      (status >= 500 && !NO_RETRY_STATUSES.includes(status));

    if (isRetryable && config && !config.__retryCount) {
      config.__retryCount = 0;
    }

    if (isRetryable && config && config.__retryCount < 3) {
      config.__retryCount += 1;
      await new Promise((resolve) =>
        setTimeout(resolve, retryDelay(config.__retryCount - 1)),
      );
      return api(config);
    }

    // After all retries exhausted: show global error toast for 5xx
    if (status && status >= 500) {
      toast.error('Erreur serveur — veuillez réessayer dans quelques instants.');
    }

    return Promise.reject(error);
  },
);
